/**
 * Base API Client with JWT Authentication
 * Role-aware client that automatically uses correct endpoints based on current role
 */

import { getStoredToken, getStoredRole, clearAuthData } from '../../utils/token'
import { isTokenExpiringSoon } from '../../utils/token'
import { UserRole } from '../../types/auth'
import { ApiError } from './types'
import { setAuthReturnTo } from '../../utils/authRedirect'

/**
 * Shared 401 handler. Captures where the user was BEFORE we clear
 * auth data and hard-reload to `/login`, so the login screen can
 * restore them after they authenticate again. Called from every
 * fetch variant below (request / postFormData / putFormData) to keep
 * behaviour consistent across GET/POST-multipart/PUT-multipart.
 */
function handleUnauthorizedRedirect(): void {
  // Skip if we're already on /login so we don't stomp on any URL the
  // user is trying to log in with, and don't overwrite a return path
  // that a prior page-load stashed.
  if (window.location.pathname === '/login') return
  const from = `${window.location.pathname}${window.location.search}${window.location.hash}`
  setAuthReturnTo(from)
  window.location.href = '/login'
}

// @ts-ignore - Vite environment variables
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

interface RequestConfig extends RequestInit {
  skipAuth?: boolean
  skipRoleRouting?: boolean // For endpoints that don't need role-based routing
}

class ApiClient {
  private baseURL: string

  constructor(baseURL: string) {
    this.baseURL = baseURL
  }

  /**
   * Get current role from localStorage
   */
  private getCurrentRole(): UserRole | null {
    return getStoredRole()
  }

  /**
   * Get JWT token from localStorage
   */
  private getToken(): string | null {
    return getStoredToken()
  }

  /**
   * Route endpoint based on role
   * If role is 'admin' → use /administrator/* or /auth/admins/*
   * If role is 'user' → use /users/* or /auth/users/*
   */
  private routeEndpointByRole(endpoint: string, role: UserRole | null): string {
    // Skip routing for endpoints that don't need it (like sign-in)
    if (endpoint.startsWith('/auth/users/sign_in') || 
        endpoint.startsWith('/auth/admins/sign_in')) {
      return endpoint
    }

    // If no role, return as-is (will fail auth anyway)
    if (!role) {
      return endpoint
    }

    // Route based on role
    if (role === 'admin') {
      // Admin endpoints use /administrator/* or /auth/admins/*
      if (endpoint.startsWith('/users/')) {
        return endpoint.replace('/users/', '/administrator/admins/')
      }
      if (endpoint.startsWith('/auth/users/')) {
        return endpoint.replace('/auth/users/', '/auth/admins/')
      }
    } else if (role === 'user') {
      // User endpoints use /users/* or /auth/users/*
      if (endpoint.startsWith('/administrator/admins/')) {
        return endpoint.replace('/administrator/admins/', '/users/')
      }
      if (endpoint.startsWith('/auth/admins/')) {
        return endpoint.replace('/auth/admins/', '/auth/users/')
      }
    }

    return endpoint
  }

  /**
   * Make API request
   */
  async request<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<T> {
    const { skipAuth = false, skipRoleRouting = false, ...fetchConfig } = config
    
    // Route endpoint based on role (unless skipped)
    const finalEndpoint = skipRoleRouting 
      ? endpoint 
      : this.routeEndpointByRole(endpoint, this.getCurrentRole())
    const finalUrl = `${this.baseURL}${finalEndpoint}`
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(fetchConfig.headers as Record<string, string> || {}),
    }

    // Add JWT token if not skipping auth
    if (!skipAuth) {
      const token = this.getToken()
      if (token) {
        // Check if token is expiring soon and trigger refresh
        if (isTokenExpiringSoon(token, 10)) {
          // Token refresh will be handled by AuthContext
          // For now, continue with current token
        }
        headers['Authorization'] = `Bearer ${token}`
      }
    }

    try {
      const response = await fetch(finalUrl, {
        ...fetchConfig,
        headers,
      })

      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401 && !skipAuth) {
        clearAuthData()
        handleUnauthorizedRedirect()
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error?.message || 'Authentication required')
      }

      // Handle other errors
      if (!response.ok) {
        const errorData: ApiError = await response.json().catch(() => ({
          error: { code: 'unknown', message: `HTTP ${response.status}` }
        }))
        throw new Error(errorData.error?.message || `HTTP ${response.status}`)
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        return await response.json()
      }
      
      return {} as T
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Network error occurred')
    }
  }

  // Convenience methods
  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'GET' })
  }

  async post<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async patch<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async put<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' })
  }

  /**
   * POST multipart/form-data (for file uploads).
   * Does NOT set Content-Type – the browser adds the boundary automatically.
   */
  async postFormData<T>(endpoint: string, formData: FormData, config: RequestConfig = {}): Promise<T> {
    const { skipAuth = false, skipRoleRouting = false } = config

    const finalEndpoint = skipRoleRouting
      ? endpoint
      : this.routeEndpointByRole(endpoint, this.getCurrentRole())
    const finalUrl = `${this.baseURL}${finalEndpoint}`

    const headers: Record<string, string> = {}

    if (!skipAuth) {
      const token = this.getToken()
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }

    try {
      const response = await fetch(finalUrl, {
        method: 'POST',
        headers,
        body: formData,
      })

      if (response.status === 401 && !skipAuth) {
        clearAuthData()
        handleUnauthorizedRedirect()
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error?.message || 'Authentication required')
      }

      if (!response.ok) {
        const errorData: ApiError = await response.json().catch(() => ({
          error: { code: 'unknown', message: `HTTP ${response.status}` }
        }))
        throw new Error(
          errorData.error?.message ||
          (typeof errorData.error === 'string' ? errorData.error : null) ||
          `HTTP ${response.status}`
        )
      }

      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        return await response.json()
      }
      return {} as T
    } catch (error) {
      if (error instanceof Error) throw error
      throw new Error('Network error occurred')
    }
  }

  async putFormData<T>(endpoint: string, formData: FormData, config: RequestConfig = {}): Promise<T> {
    const { skipAuth = false, skipRoleRouting = false } = config
    const finalEndpoint = skipRoleRouting ? endpoint : this.routeEndpointByRole(endpoint, this.getCurrentRole())
    const finalUrl = `${this.baseURL}${finalEndpoint}`
    const headers: Record<string, string> = {}
    if (!skipAuth) {
      const token = this.getToken()
      if (token) headers['Authorization'] = `Bearer ${token}`
    }
    try {
      const response = await fetch(finalUrl, { method: 'PUT', headers, body: formData })
      if (response.status === 401 && !skipAuth) {
        clearAuthData()
        handleUnauthorizedRedirect()
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error?.message || 'Authentication required')
      }
      if (!response.ok) {
        const errorData: ApiError = await response.json().catch(() => ({ error: { code: 'unknown', message: `HTTP ${response.status}` } }))
        throw new Error(errorData.error?.message || (typeof errorData.error === 'string' ? errorData.error : null) || `HTTP ${response.status}`)
      }
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) return await response.json()
      return {} as T
    } catch (error) {
      if (error instanceof Error) throw error
      throw new Error('Network error occurred')
    }
  }
}

// Export singleton instance
export const apiClient = new ApiClient(API_BASE_URL)

